/**
 * Collection filters orchestrator (HopHopShop).
 *
 * The filter aside is a left-sliding drawer below 1024px and a sticky
 * inline sidebar at lg: and up. Layout flip is CSS-only (see
 * src/input.css). Owns: drawer toggle, label sync, and filter submission.
 *
 * Submit behavior: filters are applied over fetch against the Section
 * Rendering API — the page never reloads, so the visitor keeps their
 * scroll position while the grid updates under them. At lg:+, any form
 * `change` event applies immediately (no Apply button on desktop);
 * below lg:, the Apply button in the drawer footer drives it. Sort
 * changes apply at every breakpoint. If the fetch fails we fall back to
 * a plain navigation to the same URL, which is what the form would have
 * done on its own. The chips, both "Clear all" links and the empty-state
 * clear button go through the same path — they are ordinary links, so
 * they are intercepted on click (plain left-clicks only; modifier and
 * middle clicks still open a normal tab).
 *
 * Swapped on apply: the inner HTML of [data-filters] (chips, counts,
 * checked states, Apply count) and of [data-results] (grid +
 * pagination), plus the [data-filters-count] badge and the
 * [data-results-count] total in the filter bar.
 * The aside, form, overlay and <sort-selector> elements themselves stay
 * put, so drawer state and listeners bound in setup() survive.
 *
 * Delegates: <price-range-slider> stays an independent custom element
 * and re-initializes itself when the aside content is swapped. Drawer
 * state is delegated to Drawer.controllerFor() against [data-filters] +
 * [data-filter-overlay].
 */
import { Component } from "@theme/component";
import { Drawer } from "@theme/drawer";

const SIZE_PARAM = /veli(č|c)ina|size/i;

/* ------------------------------------------------------------------------
 * API-backed filters.
 *
 * A workaround, switched on per shop (section setting "Filters via
 * Storefront API"). On hophopshop.rs Shopify's filter index for the
 * Serbian language context is broken: Liquid's collection.filters comes
 * back with four groups and a filtered page comes back empty, while the
 * same collection queried through the Storefront API in the shop's
 * default language context returns all eighteen groups and the right
 * products. So in this mode the facets and the filtered product list come
 * from the API, and the cards are still rendered by Liquid — through the
 * search page, which can be pinned to an exact set of handles
 * (`q=handle:a OR handle:b`) and whose results are fine on that shop.
 *
 * The URL stays in Shopify's own filter format, so links, chips and the
 * unfiltered first paint are unchanged, and switching the setting off
 * returns everything to the plain Liquid path. Any failure falls back to
 * that path too.
 *
 * Requests from the shop's own domain need no Storefront access token,
 * but they must carry Accept-Language: en (see query()).
 * ---------------------------------------------------------------------- */
const API_URL = "/api/2025-07/graphql.json";
const CARDS_SECTION = "api-product-cards";
const CARDS_BATCH = 50;
const COLOR_PARAM = /color|colour|boja/i;
const SORT_KEYS = {
  manual: ["COLLECTION_DEFAULT", false],
  "best-selling": ["BEST_SELLING", false],
  created: ["CREATED", true],
  "created-descending": ["CREATED", true],
  "created-ascending": ["CREATED", false],
  "price-ascending": ["PRICE", false],
  "price-descending": ["PRICE", true],
  "title-ascending": ["TITLE", false],
  "title-descending": ["TITLE", true],
};
const SEARCH_SORT_KEYS = {
  relevance: ["RELEVANCE", false],
  "price-ascending": ["PRICE", false],
  "price-descending": ["PRICE", true],
};
const SEARCH_QUERY = `query ($q: String!, $first: Int!, $after: String, $sortKey: SearchSortKeys, $reverse: Boolean, $filters: [ProductFilter!]) {
  search(query: $q, first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, productFilters: $filters, types: [PRODUCT], unavailableProducts: SHOW) {
    pageInfo { hasNextPage endCursor }
    productFilters { id label type values { label count input } }
    nodes { ... on Product { handle } }
  }
}`;
// Search trimming (see ApiFilters.searchSet). Shopify's search appends
// "semantically similar" products after the real keyword matches, so a
// search for "patike" ends in slippers, sandals and boots. The scan query
// reads the whole list once; the set query then serves facets and filtered
// products for just the matches, SEARCH_SET_CHUNK handles per request (one
// handle query stops at 250 results and returns them unordered).
const SEARCH_SCAN_QUERY = `query ($q: String!, $after: String) {
  search(query: $q, first: 250, after: $after, types: [PRODUCT], unavailableProducts: SHOW) {
    pageInfo { hasNextPage endCursor }
    nodes { ... on Product { handle title vendor productType tags } }
  }
}`;
const SEARCH_SET_QUERY = `query ($q: String!, $filters: [ProductFilter!]) {
  search(query: $q, first: 250, types: [PRODUCT], unavailableProducts: SHOW, productFilters: $filters) {
    productFilters { id label type values { label count input } }
    nodes { ... on Product { handle priceRange { minVariantPrice { amount } } } }
  }
}`;
const SEARCH_SCAN_PAGES = 4;
const SEARCH_SET_CHUNK = 100;

/** Lowercase, no diacritics; đ spelled the way shoppers type it. */
const fold = (s) =>
  String(s ?? "").toLowerCase().replace(/đ/g, "dj").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/**
 * Whether a product answers every word of the query through its title,
 * brand, type or tags. Serbian words change their endings ("crne" in the
 * query, "CRNA" in the title), so a word matches any token that starts with
 * it minus its last letter or two; words with digits (model codes) must
 * appear whole. Words of one or two letters ("za", "sa") are ignored.
 */
function searchWords(q) {
  return fold(q).split(/[^a-z0-9]+/).filter((w) => w.length > 2);
}
function matchesWords(product, words) {
  const text = fold([product.title, product.vendor, product.productType, ...(product.tags || [])].join(" "));
  const tokens = text.split(/[^a-z0-9]+/);
  const compact = text.replace(/[^a-z0-9]/g, "");
  return words.every((w) => {
    if (compact.includes(w)) return true;
    if (/\d/.test(w)) return false;
    const stem = w.length >= 6 ? w.slice(0, -2) : w.length >= 4 ? w.slice(0, -1) : w;
    return tokens.some((t) => t.startsWith(stem));
  });
}

async function storefront(query, variables, signal) {
  // The tokenless API picks its language context from the browser's
  // Accept-Language: a Serbian browser lands in the broken Serbian index
  // and gets the same four groups Liquid does. Pin the request to the
  // shop's default context instead (@inContext can't reach it: it is not a
  // published language). Cookies play no part in this.
  const response = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Accept-Language": "en" },
    body: JSON.stringify({ query, variables }),
    signal,
  });
  if (!response.ok) throw new Error(`Storefront API HTTP ${response.status}`);
  return response.json();
}

/** Raw API facets -> the shape renderFacets() reads. */
function mapFacets(facets) {
  return facets.map((f) => ({
    id: f.id,
    label: f.label,
    type: f.type,
    values: f.values.map((v) => {
      let input = null;
      try { input = JSON.parse(v.input); } catch { /* leave null */ }
      return { label: v.label, count: v.count, input, urlValue: urlValueOf(input) };
    }),
  }));
}

/**
 * Facets of disjoint product sets, added up: counts summed per value, the
 * price range widened, and groups and values kept in Shopify's order.
 */
function mergeFacets(lists) {
  const insertAfter = (arr, prevKey, item, keyOf) => {
    const at = prevKey === null ? -1 : arr.findIndex((x) => keyOf(x) === prevKey);
    arr.splice(at + 1, 0, item);
  };
  const out = [];
  for (const list of lists) {
    let prevId = null;
    for (const f of list) {
      let target = out.find((x) => x.id === f.id);
      if (!target) {
        target = { id: f.id, label: f.label, type: f.type, values: [] };
        insertAfter(out, prevId, target, (x) => x.id);
      }
      prevId = f.id;
      if (f.type === "PRICE_RANGE") {
        const cur = target.values[0];
        if (!cur) { target.values.push({ ...f.values[0] }); continue; }
        try {
          const a = JSON.parse(cur.input);
          const b = JSON.parse(f.values[0]?.input || "{}");
          a.price.min = Math.min(a.price.min ?? 0, b.price?.min ?? Infinity);
          a.price.max = Math.max(a.price.max ?? 0, b.price?.max ?? 0);
          cur.input = JSON.stringify(a);
        } catch { /* keep the first range */ }
        continue;
      }
      let prevValue = null;
      for (const v of f.values) {
        const hit = target.values.find((x) => x.input === v.input);
        if (hit) hit.count += v.count;
        else insertAfter(target.values, prevValue, { ...v }, (x) => x.input);
        prevValue = v.input;
      }
    }
  }
  return out;
}

const PRODUCTS_QUERY = `query ($handle: String!, $first: Int!, $after: String, $sortKey: ProductCollectionSortKeys, $reverse: Boolean, $filters: [ProductFilter!]) {
  collection(handle: $handle) {
    products(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, filters: $filters) {
      pageInfo { hasNextPage endCursor }
      filters { id label type values { label count input } }
      nodes { handle }
    }
  }
}`;

const CLS = {
  chip: "hhs-filter-chip inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-pink-pale text-ink font-body text-micro font-bold tracking-micro uppercase no-underline border border-noir-violet transition-colors duration-(--dur-fast) hover:bg-pink-deep hover:text-paper focus-visible:bg-pink-deep focus-visible:text-paper focus-visible:outline-none",
  clear: "ml-1 text-ink font-body text-micro font-bold tracking-eyebrow uppercase no-underline border-b border-current pb-0.5 transition-colors duration-(--dur-fast) hover:text-hophop-pink focus-visible:text-hophop-pink focus-visible:outline-none",
  summary: "hhs-fsection__head list-none cursor-pointer w-full flex justify-between items-center hhs-px py-5 font-body text-eyebrow font-bold tracking-eyebrow uppercase text-ink select-none",
  size: "hhs-size relative inline-flex items-center justify-center min-w-11 h-11 px-3 rounded-full border border-ink bg-paper text-ink font-body text-eyebrow font-semibold cursor-pointer transition-[background,color,transform] duration-(--dur-fast) ease-pop hover:-translate-y-0.5 hover:text-hophop-pink focus-within:-translate-y-0.5",
  row: "flex items-center gap-3 cursor-pointer font-body text-sm text-ink transition-colors duration-(--dur-fast) hover:text-hophop-pink focus-within:text-hophop-pink",
  box: "hhs-filter-box shrink-0 basis-4 w-4 h-4 border border-ink bg-paper inline-flex items-center justify-center text-paper transition-[background,box-shadow] duration-(--dur-fast) ease-brand peer-checked:bg-pink-pale [&_svg]:opacity-0 [&_svg]:transition-opacity [&_svg]:duration-(--dur-fast) [&_svg]:ease-brand peer-checked:[&_svg]:opacity-100",
  input: "peer absolute opacity-0 pointer-events-none",
  count: "font-mono text-xs text-fg-soft",
};

const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

/** Whether the URL asks for anything Liquid's broken index can't serve. */
function hasFilterParams(params) {
  for (const key of params.keys()) {
    if (key.startsWith("filter.") && key !== "filter.v.availability") return true;
  }
  return false;
}

/** The value Liquid would put in the URL for this API filter value. */
function urlValueOf(input) {
  if (!input) return null;
  if ("available" in input) return input.available ? "1" : "0";
  if (input.variantOption) return input.variantOption.value;
  if (input.productMetafield) {
    const v = input.productMetafield.value;
    return v === "true" ? "1" : v === "false" ? "0" : v;
  }
  return null;
}

function parseSwatchMap(raw) {
  const map = new Map();
  for (const line of String(raw || "").split(/\r?\n/)) {
    const i = line.indexOf(":");
    if (i < 0) continue;
    map.set(line.slice(0, i).trim().toLowerCase(), line.slice(i + 1).trim());
  }
  return map;
}

class ApiFilters {
  constructor(host) {
    this.host = host;
    const d = host.dataset;
    // Search pages carry the query instead of a collection handle.
    this.mode = d.searchQuery !== undefined ? "search" : "collection";
    this.searchQuery = d.searchQuery || "";
    this.handle = d.collectionHandle;
    this.perPage = Number(d.perPage) || 24;
    this.gridClass = d.gridClass || "grid grid-cols-2 md:grid-cols-3";
    this.defaultSort = d.defaultSort || "manual";
    this.paginationStyle = d.paginationStyle || "pages";
    this.currency = d.currency || "RSD";
    this.countOne = d.countOne || "";
    this.countOther = d.countOther || "";
    this.swatches = parseSwatchMap(d.swatchMap);
    // Filters shown first, in this order; the rest fold into "Više filtera".
    this.primary = String(d.primaryFilters || "").split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
    this.moreLabel = d.moreFiltersLabel || "Više filtera";
    this.groups = host.$("[data-filter-groups]");
    this.icons = {};
    host.$("template[data-api-icons]")?.content.querySelectorAll("[data-icon]").forEach((el) => {
      this.icons[el.dataset.icon] = el.innerHTML;
    });
    this.moreTpl = host.$("template[data-api-more]");
    this.emptyTpl = host.$("template[data-api-empty]");
    this.clearLabel = host.$(".hhs-filter-clear")?.textContent.trim() || "";
    this.applyBtn = host.$(".hhs-filter-apply");
    this.applyLabel = this.applyBtn?.textContent.replace(/\s*\(\d+\)\s*$/, "").trim() || "";
    this.basePromise = null;
    this.setPromise = null;
    this.setCache = null;
    this.cursors = new Map();
    this.request = null;
    this.next = null;
    this.lastParams = null;
    this.io = null;

    host.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-api-next]");
      if (!btn) return;
      e.preventDefault();
      this.loadMore();
    });
  }

  /**
   * First paint. Liquid's unfiltered grid is fine (only the facets are
   * missing), so a plain URL just gets its panel rebuilt; a URL carrying a
   * filter gets the whole thing.
   */
  async boot() {
    const params = new URLSearchParams(location.search);
    try {
      // Search pages always: Liquid's grid carries the similar-products tail.
      if (hasFilterParams(params) || this.mode === "search") {
        await this.render(location.pathname + location.search, false);
      } else {
        const data = await this.query(params, 1, null);
        this.renderFacets(data.filters, params);
        this.host.refreshSizes();
      }
    } catch (error) {
      console.warn("[collection-filters] Storefront API facets unavailable", error);
    }
  }

  async render(url, push) {
    const host = this.host;
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    host.setAttribute("aria-busy", "true");
    const params = new URLSearchParams(url.split("?")[1] || "");
    try {
      const page = Math.max(1, Number(params.get("page")) || 1);
      const after = page > 1 ? await this.cursorFor(params, page, request.signal) : null;
      const data = await this.query(params, this.perPage, after, request.signal);
      const cards = await this.cards(data.handles, request.signal);
      if (request.signal.aborted) return;
      this.renderFacets(data.filters, params, data.total);
      this.renderGrid(data, cards, false);
      this.next = data.hasNext ? data.cursor : null;
      this.lastParams = params;
      if (push) history.pushState({ hhsFilters: true }, "", url);
      host.refreshSizes();
    } finally {
      if (this.request === request) {
        this.request = null;
        host.removeAttribute("aria-busy");
      }
    }
  }

  async loadMore() {
    if (!this.next || !this.lastParams || this.request) return;
    const request = new AbortController();
    this.request = request;
    this.host.setAttribute("aria-busy", "true");
    try {
      const data = await this.query(this.lastParams, this.perPage, this.next, request.signal);
      const cards = await this.cards(data.handles, request.signal);
      if (request.signal.aborted) return;
      this.renderGrid(data, cards, true);
      this.next = data.hasNext ? data.cursor : null;
    } catch (error) {
      if (error.name !== "AbortError") console.warn("[collection-filters] load more failed", error);
    } finally {
      if (this.request === request) {
        this.request = null;
        this.host.removeAttribute("aria-busy");
      }
    }
  }

  /** Unfiltered facets, once: the map from URL values to API filter inputs. */
  baseFacets() {
    this.basePromise ??= this.query(new URLSearchParams(), 1, null).then((data) => {
      const map = new Map();
      for (const facet of data.filters) map.set(facet.id, facet);
      return map;
    });
    return this.basePromise;
  }

  /** URL params -> ProductFilter inputs. */
  async filtersFrom(params) {
    // Only a facet value needs the map; asking for it with nothing to map
    // would recurse, since the map itself comes from an unfiltered query.
    const base = hasFilterParams(params) ? await this.baseFacets() : new Map();
    const filters = [];
    const seen = new Set();
    for (const [key, raw] of params.entries()) {
      if (!key.startsWith("filter.")) continue;
      const val = raw.trim();
      if (key === "filter.v.price.gte" || key === "filter.v.price.lte") continue;
      if (key === "filter.v.availability") {
        if (!seen.has(key)) filters.push({ available: val === "1" });
        seen.add(key);
        continue;
      }
      const facet = base.get(key);
      if (!facet) continue;
      const value = facet.values.find((v) => v.urlValue === val || v.label === val);
      if (!value || seen.has(`${key}=${val}`)) continue;
      seen.add(`${key}=${val}`);
      filters.push(value.input);
    }
    if (this.host.hideSoldOut && !seen.has("filter.v.availability")) {
      filters.push({ available: true });
    }
    const gte = params.get("filter.v.price.gte");
    const lte = params.get("filter.v.price.lte");
    if (gte || lte) {
      const price = {};
      if (gte) price.min = Number(gte);
      if (lte) price.max = Number(lte);
      filters.push({ price });
    }
    return filters;
  }

  async query(params, first, after, signal) {
    const filters = await this.filtersFrom(params);
    const sort = params.get("sort_by") || this.defaultSort;
    if (this.mode === "search") {
      const set = await this.searchSet();
      if (set) return this.querySet(set, filters, sort, first, after, signal);
    }
    let query;
    let variables;
    if (this.mode === "search") {
      const [sortKey, reverse] = SEARCH_SORT_KEYS[sort] || SEARCH_SORT_KEYS.relevance;
      query = SEARCH_QUERY;
      variables = { q: this.searchQuery, first, after, sortKey, reverse, filters };
    } else {
      const [sortKey, reverse] = SORT_KEYS[sort] || SORT_KEYS.manual;
      query = PRODUCTS_QUERY;
      variables = { handle: this.handle, first, after, sortKey, reverse, filters };
    }
    const json = await storefront(query, variables, signal);
    const products = this.mode === "search" ? json.data?.search : json.data?.collection?.products;
    if (!products) throw new Error(json.errors?.[0]?.message || "Storefront API: no collection");
    const facets = this.mode === "search" ? products.productFilters : products.filters;
    return {
      handles: products.nodes.map((n) => n.handle).filter(Boolean),
      hasNext: products.pageInfo.hasNextPage,
      cursor: products.pageInfo.endCursor,
      filters: mapFacets(facets),
    };
  }

  /**
   * The products that actually match the search words, in Shopify's order,
   * or null to keep Shopify's full list: when nothing matches by title,
   * brand, type or tags (a descriptive query), the similar products are all
   * there is. Read once per page.
   */
  searchSet() {
    this.setPromise ??= (async () => {
      const words = searchWords(this.searchQuery);
      if (words.length === 0) return null;
      const matched = [];
      let after = null;
      for (let page = 0; page < SEARCH_SCAN_PAGES; page++) {
        const json = await storefront(SEARCH_SCAN_QUERY, { q: this.searchQuery, after });
        const found = json.data?.search;
        if (!found) throw new Error(json.errors?.[0]?.message || "Storefront API: no search");
        for (const p of found.nodes) if (p.handle && matchesWords(p, words)) matched.push(p.handle);
        if (!found.pageInfo.hasNextPage) break;
        after = found.pageInfo.endCursor;
      }
      return matched.length ? matched : null;
    })();
    return this.setPromise;
  }

  /**
   * One filter state over the matched set: every chunk queried with the
   * filters, products put back in Shopify's relevance order (or by price),
   * facets added up. Pages are cut locally; the cursor is an offset.
   */
  async querySet(set, filters, sort, first, after, signal) {
    const key = JSON.stringify([filters, sort]);
    if (this.setCache?.key !== key) {
      const chunks = [];
      for (let i = 0; i < set.length; i += SEARCH_SET_CHUNK) chunks.push(set.slice(i, i + SEARCH_SET_CHUNK));
      const results = await Promise.all(
        chunks.map(async (chunk) => {
          const q = chunk.map((h) => `handle:${h}`).join(" OR ");
          const json = await storefront(SEARCH_SET_QUERY, { q, filters }, signal);
          if (!json.data?.search) throw new Error(json.errors?.[0]?.message || "Storefront API: no search");
          return json.data.search;
        }),
      );
      const rank = new Map(set.map((h, i) => [h, i]));
      const price = new Map();
      const handles = [];
      for (const r of results) {
        for (const n of r.nodes) {
          if (!n.handle || !rank.has(n.handle) || price.has(n.handle)) continue;
          price.set(n.handle, Number(n.priceRange?.minVariantPrice?.amount) || 0);
          handles.push(n.handle);
        }
      }
      const [sortKey, reverse] = SEARCH_SORT_KEYS[sort] || SEARCH_SORT_KEYS.relevance;
      handles.sort((a, b) =>
        sortKey === "PRICE"
          ? (price.get(a) - price.get(b)) * (reverse ? -1 : 1) || rank.get(a) - rank.get(b)
          : rank.get(a) - rank.get(b),
      );
      this.setCache = { key, handles, filters: mapFacets(mergeFacets(results.map((r) => r.productFilters))) };
    }
    const { handles, filters: facets } = this.setCache;
    const start = Number(after) || 0;
    const end = start + first;
    return {
      handles: handles.slice(start, end),
      hasNext: end < handles.length,
      cursor: String(end),
      filters: facets,
      // The availability facet can't give the count here: over a handle list
      // it comes back at double the product count.
      total: handles.length,
    };
  }

  /** The cursor that opens page N of this filter state, walking forward once. */
  async cursorFor(params, page, signal) {
    const filters = await this.filtersFrom(params);
    const key = JSON.stringify([filters, params.get("sort_by") || ""]);
    const list = this.cursors.get(key) || [null];
    while (list.length < page) {
      const data = await this.query(params, this.perPage, list[list.length - 1], signal);
      if (!data.hasNext) break;
      list.push(data.cursor);
    }
    this.cursors.set(key, list);
    return list[Math.min(page, list.length) - 1];
  }

  /** Liquid-rendered cards for these handles, in this order. */
  async cards(handles, signal) {
    const batches = [];
    for (let i = 0; i < handles.length; i += CARDS_BATCH) batches.push(handles.slice(i, i + CARDS_BATCH));
    const docs = await Promise.all(
      batches.map(async (batch) => {
        const q = batch.map((h) => `handle:${h}`).join(" OR ");
        // The search page hides sold-out products by default; the API list
        // already decided what belongs here, so ask for every card.
        const response = await fetch(
          `/search?type=product&options[unavailable_products]=show&section_id=${CARDS_SECTION}&q=${encodeURIComponent(q)}`,
          { signal },
        );
        if (!response.ok) throw new Error(`Cards HTTP ${response.status}`);
        return new DOMParser().parseFromString(await response.text(), "text/html");
      }),
    );
    const byHandle = new Map();
    let style = null;
    for (const doc of docs) {
      style ??= doc.querySelector("[data-api-cards] style");
      doc.querySelectorAll("[data-api-card]").forEach((wrap) => {
        const card = wrap.querySelector("product-card");
        if (card && !byHandle.has(wrap.dataset.handle)) byHandle.set(wrap.dataset.handle, card);
      });
    }
    return { style, cards: handles.map((h) => byHandle.get(h)).filter(Boolean) };
  }

  renderGrid(data, { style, cards }, append) {
    const results = this.host.results;
    if (!append) {
      results.innerHTML = "";
      if (cards.length === 0) {
        if (this.emptyTpl) results.appendChild(this.emptyTpl.content.cloneNode(true));
        return;
      }
      if (style) results.appendChild(document.adoptNode(style));
      const grid = document.createElement("div");
      grid.setAttribute("data-product-grid", "");
      grid.className = this.gridClass;
      results.appendChild(grid);
    }
    const grid = results.querySelector("[data-product-grid]");
    if (!grid) return;
    for (const card of cards) grid.appendChild(document.adoptNode(card));
    results.querySelector("[data-api-more-wrap]")?.remove();
    this.io?.disconnect();
    if (data.hasNext && this.moreTpl) {
      results.appendChild(this.moreTpl.content.cloneNode(true));
      if (this.paginationStyle === "infinite" && window.IntersectionObserver) {
        const wrap = results.querySelector("[data-api-more-wrap]");
        this.io = new IntersectionObserver(
          (entries) => { if (entries.some((e) => e.isIntersecting)) this.loadMore(); },
          { rootMargin: "600px 0px" },
        );
        this.io.observe(wrap);
      }
    }
  }

  /** Which values the URL has switched on, per filter id. */
  activeFrom(params) {
    const active = new Map();
    for (const [key, val] of params.entries()) {
      if (!key.startsWith("filter.")) continue;
      if (!active.has(key)) active.set(key, new Set());
      active.get(key).add(val.trim());
    }
    return active;
  }

  hrefWithout(params, key, value) {
    const next = new URLSearchParams();
    for (const [k, v] of params.entries()) {
      if (k === key && (value === undefined || v.trim() === value)) continue;
      if (k === "page") continue;
      next.append(k, v);
    }
    const q = next.toString();
    return q ? `${this.host.baseUrl}?${q}` : this.host.baseUrl;
  }

  renderFacets(filters, params, knownTotal = null) {
    if (!this.groups) return;
    const active = this.activeFrom(params);
    const chips = [];
    const groups = [];
    let activeCount = 0;
    let total = null;

    filters.forEach((facet, index) => {
      if (facet.id === "filter.v.availability") {
        const wanted = active.get(facet.id);
        total = facet.values.reduce((sum, v) => {
          if (wanted && !wanted.has(v.urlValue)) return sum;
          if (!wanted && this.host.hideSoldOut && v.urlValue !== "1") return sum;
          return sum + v.count;
        }, 0);
        return;
      }
      const on = active.get(facet.id) || new Set();
      let body = "";
      let hasActive = false;

      if (facet.type === "PRICE_RANGE") {
        const range = facet.values[0]?.input?.price || {};
        const max = Math.ceil(Number(range.max) || 0);
        const gte = params.get("filter.v.price.gte") || "";
        const lte = params.get("filter.v.price.lte") || "";
        hasActive = !!(gte || lte);
        if (hasActive) {
          activeCount += 1;
          chips.push(
            `<a href="${esc(this.hrefWithout(this.hrefWithoutParams(params, ["filter.v.price.gte"]), "filter.v.price.lte"))}" class="${CLS.chip}">${esc(this.currency)} ${esc(gte || 0)}&nbsp;–&nbsp;${esc(this.currency)} ${esc(lte || max)}${this.icons.close || ""}</a>`,
          );
        }
        body = `<price-range-slider data-range-max="${max}" data-currency="${esc(this.currency)}" class="hhs-fsection__range block w-full">
          <div data-range-slider><div data-range-track></div><div data-range-min></div><div data-range-max></div></div>
          <div class="w-full flex justify-between items-center gap-2 font-mono text-eyebrow text-ink mt-0">
            <span data-label-min>${esc(this.currency)}&nbsp;${esc(Math.round(Number(gte) || 0))}</span>
            <span data-label-max class="inline-block whitespace-nowrap">${esc(this.currency)}&nbsp;${esc(Math.round(Number(lte) || max))}+</span>
          </div>
          <input type="hidden" name="filter.v.price.gte" data-price-min${gte ? ` value="${esc(gte)}"` : ""}>
          <input type="hidden" name="filter.v.price.lte" data-price-max${lte ? ` value="${esc(lte)}"` : ""}>
        </price-range-slider>`;
      } else if (facet.type === "BOOLEAN") {
        const checked = on.has("1");
        hasActive = checked;
        if (checked) {
          activeCount += 1;
          chips.push(`<a href="${esc(this.hrefWithout(params, facet.id))}" class="${CLS.chip}">${esc(facet.label)}${this.icons.close || ""}</a>`);
        }
        body = `<label class="${CLS.row}">
          <input type="checkbox" name="${esc(facet.id)}" value="1" class="${CLS.input}"${checked ? " checked" : ""}>
          <span style="border-color:#c0c0c0;" class="${CLS.box} rounded-full" aria-hidden="true"></span>
          <span class="flex-1">${esc(facet.label)}</span>
        </label>`;
      } else {
        const isSize = SIZE_PARAM.test(facet.label) || SIZE_PARAM.test(facet.id);
        const isColor = COLOR_PARAM.test(facet.label) || COLOR_PARAM.test(facet.id);
        const items = [];
        for (const value of facet.values) {
          const key = value.urlValue ?? value.label;
          const checked = on.has(key) || on.has(value.label);
          if (!(value.count > 0 || checked)) continue;
          if (checked) {
            hasActive = true;
            activeCount += 1;
            chips.push(`<a href="${esc(this.hrefWithout(params, facet.id, key))}" class="${CLS.chip}">${esc(value.label)}${this.icons.close || ""}</a>`);
          }
          const input = `<input type="checkbox" name="${esc(facet.id)}" value="${esc(key)}" class="${isSize ? "absolute opacity-0 pointer-events-none" : CLS.input}"${checked ? " checked" : ""}${value.count === 0 ? " disabled" : ""}>`;
          if (isSize) {
            items.push(`<label${checked ? "" : ' style="border-color:#c0c0c0;"'} class="${CLS.size}${checked ? " is-active bg-pink-pale text-ink -translate-y-0.5" : " hover:border-hophop-pink"}${value.count === 0 ? " is-oos line-through opacity-40 cursor-not-allowed hover:translate-y-0 hover:shadow-none" : ""}">${input}<span>${esc(value.label)}</span></label>`);
          } else if (isColor) {
            const hex = this.swatches.get(value.label.toLowerCase().trim());
            items.push(`<li><label class="${CLS.row}">${input}<span style="border-radius:4px;border-width:1px;border-color:#c0c0c0;" class="${CLS.box}" aria-hidden="true"></span>${hex ? `<span style="display:inline-block;flex-shrink:0;width:14px;height:14px;border-radius:50%;background-color:${esc(hex)};border:1.5px solid rgba(0,0,0,0.15);"></span>` : ""}<span class="flex-1">${esc(value.label)}</span><span class="${CLS.count}">${value.count}</span></label></li>`);
          } else {
            items.push(`<li><label class="${CLS.row}">${input}<span style="border-color:#c0c0c0;" class="${CLS.box} rounded-full" aria-hidden="true"></span><span class="flex-1">${esc(value.label)}</span><span class="${CLS.count}">${value.count}</span></label></li>`);
          }
        }
        if (items.length === 0) return;
        body = isSize
          ? `<div class="flex flex-wrap gap-2">${items.join("")}</div>`
          : `<ul class="list-none m-0 p-0 flex flex-col gap-3">${items.join("")}</ul>`;
      }

      groups.push({ label: facet.label, index, hasActive, body });
    });

    const toggle = `<span class="inline-flex items-center" aria-hidden="true"><span class="inline-flex group-open:hidden">${this.icons.plus || "+"}</span><span class="hidden group-open:inline-flex">${this.icons.minus || "−"}</span></span>`;
    const groupHtml = (g, open) => `<details class="group border-b border-border-soft"${open ? " open" : ""}>
        <summary class="${CLS.summary}"><span>${esc(g.label).toUpperCase()}</span>${toggle}</summary>
        <div class="hhs-px pb-6">${g.body}</div>
      </details>`;
    let groupsHtml;
    if (this.primary.length) {
      const rank = (g) => this.primary.indexOf(String(g.label).trim().toLowerCase());
      const first = groups.filter((g) => rank(g) >= 0).sort((a, b) => rank(a) - rank(b));
      const rest = groups.filter((g) => rank(g) < 0);
      groupsHtml = first.map((g, i) => groupHtml(g, i < 3 || g.hasActive)).join("");
      if (rest.length) {
        const restActive = rest.some((g) => g.hasActive);
        groupsHtml += `<details class="group hhs-more-filters border-b border-border-soft"${restActive ? " open" : ""}>
          <summary class="${CLS.summary}"><span>${esc(this.moreLabel).toUpperCase()}</span>${toggle}</summary>
          <div class="hhs-more-filters__body">${rest.map((g) => groupHtml(g, g.hasActive)).join("")}</div>
        </details>`;
      }
    } else {
      groupsHtml = groups.map((g) => groupHtml(g, g.index < 3 || g.hasActive)).join("");
    }

    // "Clear all" keeps what identifies the page (the search terms) and the
    // sold-out rider, drops every filter and the sort, like Liquid's link.
    const keep = new URLSearchParams();
    for (const [k, v] of params.entries()) if (k === "q" || k === "type") keep.append(k, v);
    if (this.host.hideSoldOut) keep.append("filter.v.availability", "1");
    const clearQuery = keep.toString();
    const clearHref = clearQuery ? `${this.host.baseUrl}?${clearQuery}` : this.host.baseUrl;
    const chipsHtml = chips.length
      ? `<div class="hhs-px pb-4 flex gap-1.5 flex-wrap items-center">${chips.join("")}<a href="${esc(clearHref)}" class="${CLS.clear}">${esc(this.clearLabel)}</a></div>`
      : "";

    const openState = new Map();
    this.groups.querySelectorAll("details").forEach((g) => {
      const label = g.querySelector("summary span")?.textContent.trim();
      if (label) openState.set(label, g.open);
    });
    this.groups.innerHTML = chipsHtml + groupsHtml;
    this.groups.querySelectorAll("details").forEach((g) => {
      const label = g.querySelector("summary span")?.textContent.trim();
      if (label && openState.has(label)) g.open = openState.get(label);
    });

    const host = this.host;
    if (host.countBadge) host.countBadge.textContent = activeCount > 0 ? `(${activeCount})` : "";
    if (this.applyBtn) this.applyBtn.textContent = activeCount > 0 ? `${this.applyLabel} (${activeCount})` : this.applyLabel;
    if (knownTotal !== null) total = knownTotal;
    if (host.resultsCount && total !== null) {
      const tpl = total === 1 ? this.countOne : this.countOther;
      if (tpl) host.resultsCount.textContent = tpl.replace(/99999|\b1\b/, String(total));
    }
    const sort = params.get("sort_by") || this.defaultSort;
    const sortSelector = host.$("sort-selector");
    if (typeof sortSelector?.sync === "function") sortSelector.sync(sort);
  }

  hrefWithoutParams(params, keys) {
    const next = new URLSearchParams();
    for (const [k, v] of params.entries()) if (!keys.includes(k)) next.append(k, v);
    return next;
  }
}


class CollectionFilters extends Component {
  #onChange;
  #request;
  #realSizesPromise;

  setup() {
    this.form = this.$("[data-filter-form]");
    this.aside = this.$("[data-filters]");
    this.overlay = this.$("[data-filter-overlay]");
    this.label = this.$("[data-filters-label]");
    this.results = this.$("[data-results]");
    this.hideSoldOut = this.dataset.hideSoldOut === "true";
    // Whether Shopify's Availability filter is switched on in Search &
    // Discovery. Without it the parameter below is inert, so adding it only
    // puts noise in the shopper's address bar.
    this.availabilityFilter = this.dataset.availabilityFilter === "true";
    this.countBadge = this.$("[data-filters-count]");
    this.resultsCount = this.$("[data-results-count]");

    this.sectionId =
      this.closest(".shopify-section")?.id.replace("shopify-section-", "") ??
      null;
    this.baseUrl = this.form?.getAttribute("action") || location.pathname;

    this.drawer = Drawer.controllerFor(this.aside, this.overlay, this);

    new MutationObserver(() => this.#updateLabel()).observe(this.aside, {
      attributes: true,
      attributeFilter: ["class"],
    });

    this.$$("[data-toggle-filters]").forEach((btn) =>
      btn.addEventListener("click", () => this.toggle()),
    );
    this.#bindCloseButtons();

    this.#updateLabel();

    this.desktopMq = window.matchMedia("(min-width: 1024px)");
    this.#onChange = (e) => {
      const size = e.target.closest?.(".hhs-size");
      if (size) this.#syncSize(size, e.target.checked);
      // Sort is a single-click choice with no Apply button of its own, so it
      // applies on every breakpoint; the filter inputs wait for Apply on mobile.
      const isSort = !!e.target.closest?.("sort-selector");
      if (!isSort && !this.desktopMq.matches) return;
      this.form?.requestSubmit();
    };
    this.form?.addEventListener("change", this.#onChange);

    this.form?.addEventListener("submit", (e) => {
      // Without a section id we can't render a partial — let the form submit.
      if (!this.sectionId || !this.results) return;
      e.preventDefault();
      this.#apply();
    });

    // Remove-a-value chips, "Clear all" and the empty-state clear button are
    // plain links; route them through the same swap so they don't navigate.
    this.addEventListener("click", (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = e.target.closest("a[href]");
      if (!link || !this.#isFilterLink(link)) return;
      if (!this.sectionId || !this.results) return;
      e.preventDefault();
      this.drawer.close();
      this.#render(
        this.#withoutStaleAvailability(link.getAttribute("href")),
        true,
      );
    });

    window.addEventListener("popstate", () => {
      if (!this.sectionId || location.pathname !== this.baseUrl) return;
      this.#render(location.pathname + location.search, false);
    });

    this.#pruneSizes();

    // See ApiFilters above. Owns the facets and the filtered grid when the
    // section setting is on; everything else here keeps working unchanged.
    this.api = this.dataset.apiFilters === "true" && this.$("[data-filter-groups]")
      ? new ApiFilters(this)
      : null;
    this.api?.boot();
  }

  /** Re-run the size sweep after someone else rebuilt the panel. */
  refreshSizes() {
    return this.#pruneSizes();
  }

  toggle() {
    this.drawer.isOpen ? this.drawer.close() : this.drawer.open();
  }

  /**
   * A size pill's selected look is server-rendered (is-active, no grey inline
   * border) and so only appears once the filter is applied. On mobile that
   * waits for the Apply button, so mirror the markup for an applied value
   * here — otherwise a tapped size looks exactly like an untapped one.
   */
  #syncSize(label, on) {
    label.classList.toggle("is-active", on);
    label.classList.toggle("bg-pink-pale", on);
    label.classList.toggle("-translate-y-0.5", on);
    label.classList.toggle("hover:border-hophop-pink", !on);
    label.style.borderColor = on ? "" : "#c0c0c0";
  }

  /**
   * url_to_remove links from Shopify keep every other param, including the
   * availability rider #urlFromForm adds alongside sizes. When the last size
   * is being removed, drop the rider too so it can't keep filtering the grid
   * invisibly.
   */
  #withoutStaleAvailability(href) {
    const url = new URL(href, location.origin);
    const params = url.searchParams;
    // When the section hides sold-out products outright the rider is not
    // stale, it is the point — leave it wherever it appears.
    if (this.hideSoldOut) return url.pathname + url.search;
    if (params.has("filter.v.availability")) {
      let hasSize = false;
      for (const key of params.keys()) {
        if (key.startsWith("filter.v.option.") && SIZE_PARAM.test(key)) {
          hasSize = true;
          break;
        }
      }
      if (!hasSize) params.delete("filter.v.availability");
    }
    return url.pathname + url.search;
  }

  /** Apply the current form state: fetch, swap, push the URL. */
  #apply() {
    this.drawer.close();
    this.#render(this.#urlFromForm(), true);
  }

  /**
   * Collection URL for the current form state. Empty values are dropped so
   * an untouched price slider doesn't end up in the query, and `page` is
   * absent by design — a filter change sends you back to page 1.
   *
   * Picking a size quietly adds `filter.v.availability=1`: Shopify matches
   * option + availability on the same variant, so "37" then means "37 in
   * stock" instead of "any product that ever had a 37". The availability
   * filter itself is never rendered — it only rides along here. (Requires
   * the Availability filter to be enabled in Search & Discovery.)
   */
  #urlFromForm() {
    const params = new URLSearchParams();
    let hasSizeFilter = false;
    for (const [key, value] of new FormData(this.form).entries()) {
      if (typeof value !== "string" || value === "") continue;
      params.append(key, value);
      if (key.startsWith("filter.v.option.") && SIZE_PARAM.test(key)) {
        hasSizeFilter = true;
      }
    }
    if (
      this.availabilityFilter &&
      (this.hideSoldOut || hasSizeFilter) &&
      !params.has("filter.v.availability")
    ) {
      params.append("filter.v.availability", "1");
    }
    const query = params.toString();
    return query ? `${this.baseUrl}?${query}` : this.baseUrl;
  }

  async #render(url, push) {
    if (this.api) {
      try {
        await this.api.render(url, push);
        return;
      } catch (error) {
        if (error.name === "AbortError") return;
        console.warn("[collection-filters] API render failed, using Liquid", error);
      }
    }
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.setAttribute("aria-busy", "true");

    const separator = url.includes("?") ? "&" : "?";
    const sectionUrl = `${url}${separator}section_id=${encodeURIComponent(this.sectionId)}`;

    try {
      const response = await fetch(sectionUrl, { signal: request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      // A password page, a challenge or an app interstitial answers 200 with
      // markup that has no results in it — treat that as a failed render
      // rather than silently leaving the old grid on screen.
      if (!this.#swap(doc)) throw new Error("No results in response");
      if (push) history.pushState({ hhsFilters: true }, "", url);
      // The swap re-rendered the full size list — hide the dead sizes again.
      this.#pruneSizes();
    } catch (error) {
      if (error.name === "AbortError") return;
      // Network or section-render failure: do what the plain form would do.
      location.assign(url);
      return;
    } finally {
      // A newer apply may already be in flight — it owns the busy state now.
      if (this.#request === request) {
        this.#request = null;
        this.removeAttribute("aria-busy");
      }
    }
  }

  /**
   * Hide size values with no in-stock variant anywhere in the collection.
   * Liquid can't compute this (filter counts ignore stock, and
   * collection.products caps at 50), so the collection's own products.json
   * is swept once per page load and the sizes with a purchasable variant
   * are collected; pills outside that set are hidden. Checked pills always
   * stay so they can be unchecked.
   */
  async #pruneSizes() {
    // Search results have no products.json to sweep.
    if (!this.aside || this.dataset.pruneSizes === "false") return;
    const alive = await this.#realSizes();
    if (!alive || alive.size === 0) return;
    this.aside
      .querySelectorAll('input[name^="filter.v.option."]')
      .forEach((input) => {
        if (!SIZE_PARAM.test(input.name)) return;
        const label = input.closest("label");
        if (!label) return;
        const dead = !input.checked && !alive.has(input.value.trim());
        // style.display, not [hidden]: the pill's display class outranks
        // the UA rule for the hidden attribute.
        label.style.display = dead ? "none" : "";
      });
  }

  /**
   * The set of size values with at least one available variant in this
   * collection, from /products.json (250 per page, up to 8 pages). Memoized:
   * the answer is collection-wide, so filter changes don't re-fetch. Returns
   * null — prune nothing — when the sweep can't be trusted: a fetch failed,
   * or the collection is bigger than the sweep and a real size might live in
   * the unseen tail.
   */
  async #realSizes() {
    this.#realSizesPromise ??= (async () => {
      try {
        const sizes = new Set();
        for (let page = 1; page <= 8; page++) {
          const response = await fetch(
            `${this.baseUrl}/products.json?limit=250&page=${page}`,
          );
          if (!response.ok) return null;
          const { products } = await response.json();
          for (const product of products ?? []) {
            const options = product.options ?? [];
            const sizeOption = options.find((option) =>
              SIZE_PARAM.test(
                typeof option === "string" ? option : (option?.name ?? ""),
              ),
            );
            if (!sizeOption) continue;
            const position =
              typeof sizeOption === "string"
                ? options.indexOf(sizeOption) + 1
                : (sizeOption.position ?? options.indexOf(sizeOption) + 1);
            for (const variant of product.variants ?? []) {
              const value = variant[`option${position}`];
              if (variant.available && value != null) {
                sizes.add(String(value).trim());
              }
            }
          }
          if (!products || products.length < 250) return sizes;
        }
        return null;
      } catch {
        return null;
      }
    })();
    return this.#realSizesPromise;
  }

  /** @returns {boolean} whether the response actually carried a new grid. */
  #swap(doc) {
    const aside = doc.querySelector("[data-filters]");
    const results = doc.querySelector("[data-results]");
    const badge = doc.querySelector("[data-filters-count]");
    const resultsCount = doc.querySelector("[data-results-count]");
    if (!results) return false;

    if (aside) {
      const openState = this.#groupState();
      this.aside.innerHTML = aside.innerHTML;
      this.#restoreGroupState(openState);
      this.#bindCloseButtons();
    }
    this.results.innerHTML = results.innerHTML;
    if (badge && this.countBadge)
      this.countBadge.textContent = badge.textContent;
    if (resultsCount && this.resultsCount)
      this.resultsCount.textContent = resultsCount.textContent;

    // "Clear all" drops sort_by along with the filters, so the selector has to
    // follow whatever sort the server actually rendered.
    const nextSort = doc.querySelector(
      "sort-selector [data-sort-input]",
    )?.value;
    const sortSelector = this.$("sort-selector");
    if (nextSort && typeof sortSelector?.sync === "function")
      sortSelector.sync(nextSort);

    return true;
  }

  /**
   * Links that change the filter state: everything inside the aside (the
   * removable chips and both "Clear all" links) plus the empty-state button.
   * Product and pagination links are left to navigate normally.
   */
  #isFilterLink(link) {
    return this.aside.contains(link) || link.hasAttribute("data-clear-filters");
  }

  /**
   * Which filter groups the visitor has open, keyed by label — the swap would
   * otherwise snap every <details> back to its server-rendered default.
   */
  #groupState() {
    const state = new Map();
    this.aside.querySelectorAll("details").forEach((group) => {
      const label = group.querySelector("summary span")?.textContent.trim();
      if (label) state.set(label, group.open);
    });
    return state;
  }

  /** Groups the server sent that weren't on screen before keep their default. */
  #restoreGroupState(state) {
    this.aside.querySelectorAll("details").forEach((group) => {
      const label = group.querySelector("summary span")?.textContent.trim();
      if (label && state.has(label)) group.open = state.get(label);
    });
  }

  /** (Re)bind the drawer close buttons — they live inside the swapped aside. */
  #bindCloseButtons() {
    this.$$("[data-close-filters]").forEach((btn) => {
      if (btn.dataset.closeBound === "1") return;
      btn.dataset.closeBound = "1";
      btn.addEventListener("click", () => this.drawer.close());
    });
  }

  #updateLabel() {
    if (!this.label) return;
    this.label.textContent = this.drawer.isOpen ? "Hide filters" : "Filter";
  }
}

customElements.define("collection-filters", CollectionFilters);

export { CollectionFilters };
